import { zodResolver } from "@hookform/resolvers/zod"
import { DUTCH_WEEK, gtmStoreLocatorSearchSubmitted, typedBoolean } from "@jog/shared"
import type { JoggStore } from "@jog/types"
import { format, isAfter, isBefore } from "date-fns"
import { fromZonedTime } from "date-fns-tz"
import { type PropsWithChildren, createContext, useCallback, useContext, useEffect, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { z } from "zod"

import { usePending } from "../components"
import { useUserLocation } from "./useLocation"

const MapContext = createContext<typeof woosmap>({} as typeof woosmap)

export const WoosmapAPIProvider = ({ children }: PropsWithChildren) => {
    const [isLoaded, setIsLoaded] = useState(false)

    useEffect(() => {
        const apiKey = process.env.NEXT_PUBLIC_WOOSMAP_KEY
        if (!apiKey) {
            throw new Error("Woosmap API key is not set")
        }
        const script = document.createElement("script")
        script.src = `https://sdk.woosmap.com/map/map.js?key=${apiKey}`
        script.onload = () => setIsLoaded(true)
        document.body.appendChild(script)
    }, [])

    if (!isLoaded) {
        return null
    }

    return <MapContext.Provider value={woosmap}>{children}</MapContext.Provider>
}

export const useWoosmap = <Ref extends HTMLElement>(zoom: number = 8) => {
    const ref = useRef<Ref>(null)
    const woosmap = useContext(MapContext)
    const map = useRef<woosmap.map.Map | null>(null)

    useEffect(() => {
        if (ref.current && !map.current) {
            map.current = new woosmap.map.Map(ref.current, { zoom, center: { lat: 52.370216, lng: 4.895168 } })
        }
    }, [woosmap.map.Map, zoom])
    return { ref, map }
}

export const useJoggStoreMakers = <
    Ref extends HTMLElement,
    Store extends Pick<JoggStore, "storeNumber" | "latitude" | "longitude">,
>({
    stores,
    activeStore,
    setActiveStore,
}: {
    stores: Store[]
    activeStore: string
    setActiveStore: (storeNumber: string) => void
}) => {
    const markerMap = useRef<Record<string, woosmap.map.Marker>>({})
    const { ref: mapRef, map } = useWoosmap<Ref>()
    const [mapLoaded, setMapLoaded] = useState(false)
    const size = useRef<number>(40)
    const infoWindow = useRef<woosmap.map.InfoWindow | null>(null)
    const [infoWindowContent, setInfoWindowContent] = useState<HTMLElement | null>(null)

    useEffect(() => {
        if (!map.current) return
        const event = map.current.addListener("zoom_changed", () => {
            const zoom = map.current?.getZoom()
            if (!zoom) return
            const currentSize = Math.min(Math.floor(Math.max(zoom / 3, 1)) * 12, 40)
            if (currentSize === size.current) return
            size.current = currentSize
            Object.entries(markerMap.current).forEach(([storeNumber, marker]) => {
                marker.setIcon({
                    url: storeNumber === activeStore ? `/images/pin-active.png?w=40&h=40` : `/images/pin.png?w=40&h=40`,
                    scaledSize: { height: currentSize, width: currentSize },
                })
            })
            if (infoWindow.current) {
                infoWindow.current.setOffset(new woosmap.map.Point(-currentSize / 2, currentSize / 2))
            }
        })
        return () => {
            event.remove()
        }
    }, [activeStore, map])
    useEffect(() => {
        if (!map.current) return
        const event = map.current.addListener("idle", () => {
            const zoom = map.current?.getZoom()
            if (!zoom) return
            size.current = Math.min(Math.floor(Math.max(zoom / 3, 1)) * 12, 40)
            setMapLoaded(true)
        })
        return () => {
            event.remove()
        }
    }, [map])
    useEffect(() => {
        if (!map.current) return
        if (!mapLoaded) return
        if (!stores?.length) return
        Object.values(markerMap.current).forEach((marker) => {
            marker.setMap(null)
        })
        markerMap.current = {}
        const eventMap = new Map<string, ReturnType<woosmap.map.Marker["addListener"]>>()
        stores.forEach((store) => {
            const marker =
                markerMap.current[store.storeNumber] ||
                new woosmap.map.Marker({ position: { lat: Number(store.latitude), lng: Number(store.longitude) } })
            marker.setIcon({
                url:
                    store.storeNumber === activeStore
                        ? `/images/pin-active.png?w=40&h=40`
                        : `/images/pin.png?w=40&h=40`,
                scaledSize: { height: size.current, width: size.current },
            })
            marker.setMap(map.current)
            markerMap.current[store.storeNumber] = marker
            const event = marker.addListener("click", () => {
                setActiveStore(store.storeNumber)
            })
            eventMap.set(store.storeNumber, event)
        })
        return () => {
            eventMap.forEach((event) => {
                event.remove()
            })
        }
    }, [activeStore, map, stores, mapLoaded, size, setActiveStore])

    useEffect(() => {
        if (!mapLoaded) return
        if (infoWindow.current) return
        infoWindow.current = new woosmap.map.InfoWindow({})
        const id = `woosmap-info-window`
        infoWindow.current.setContent(`<div id="${id}"></div>`)
        infoWindow.current.setOffset(new woosmap.map.Point(-size.current / 2, size.current / 2))
        const event = infoWindow.current.addListener("domready", () => {
            const infowindow = document.getElementById(id)
            setInfoWindowContent(infowindow)
        })
        return () => {
            event.remove()
        }
    }, [mapLoaded])

    useEffect(() => {
        if (!map.current) return
        if (!mapLoaded) return
        if (!infoWindow.current) return
        infoWindow.current.close()
        const store = markerMap.current[activeStore]
        if (!store) return
        const position = store.getPosition()
        infoWindow.current.open(map.current, position)
        map.current.flyTo({ animate: true, center: position, offset: new woosmap.map.Point(100, 100), zoom: 10 })
    }, [activeStore, map, mapLoaded])

    return { infoWindowContent, mapRef }
}

const searchSchema = z.object({ search: z.string().optional() })
export const useWoosmapSearch = (searchAliases: { input: string; point_to: string }[]) => {
    const form = useForm<z.infer<typeof searchSchema>>({
        resolver: zodResolver(searchSchema),
        defaultValues: { search: "" },
    })
    const { setPending } = usePending()
    const [stores, setStores] = useState<{ storeNumber: string; woosmapDistance: number }[] | null>(null)
    const onSearch = form.handleSubmit((data: z.infer<typeof searchSchema>) => {
        if (!data.search) return
        const address =
            searchAliases.find((alias) => alias.input.toLowerCase() === data.search?.toLowerCase())?.point_to ||
            data.search
        form.setValue("search", address)
        gtmStoreLocatorSearchSubmitted(address)
        setPending(true)
        new woosmap.map.LocalitiesService()
            .geocode({ address, components: { country: ["NL", "BE", "DE", "LU"] } })
            .then(({ results }) => {
                const latLng = results.at(0)?.geometry?.location
                if (!latLng) {
                    form.setError("search", { message: "No results. Try a different postal code or city." })
                    return
                }
                return new woosmap.map.StoresService().search({ storesByPage: 300, latLng }).then(({ features }) => {
                    const stores = features
                        .map(({ properties: { store_id, distance } }) => {
                            if (!distance) return null
                            return { storeNumber: store_id, woosmapDistance: distance }
                        })
                        .filter(typedBoolean)
                    setStores(stores)
                })
            })
            .catch((error) => {
                console.log(error)
                form.setError("search", { message: "No results. Try a different postal code or city." })
            })
            .finally(() => {
                setPending(false)
            })
    })

    const { getLocation } = useUserLocation()
    const onMyLocation = useCallback(async () => {
        const city = await getLocation().catch((e) => {
            console.log("error", e)
            form.setError("search", {
                message: "Location not available. Allow location use in your browser settings.",
            })
        })
        if (!city) return
        form.setValue("search", city)
        onSearch()
    }, [getLocation, form, onSearch])
    return { form, onSearch, stores, onMyLocation }
}
const isOpen = (now: Date, opens: string, closes: string) => {
    const day = format(now, "yyyy-MM-dd")
    const [AmsterdamOpen, AmsterdamClose] = [
        fromZonedTime(`${day} ${opens.replace(".", ":")}`, "Europe/Amsterdam"),
        fromZonedTime(`${day} ${closes.replace(".", ":")}`, "Europe/Amsterdam"),
    ]
    if (isBefore(now, AmsterdamOpen)) return "ready"
    if (isAfter(now, AmsterdamClose)) return "closed"
    return "opening"
}

export const getOpenings = (storeOpenings: JoggStore["openings"]) => {
    const now = new Date()
    const day = now.getDay() // 0-6 Sunday - Saturday
    return DUTCH_WEEK.map((dayOfWeek, index) => {
        const [opens, closes] = storeOpenings[dayOfWeek].split("-").map((i) => i.trim())
        const isToday = index === 6 ? day === 0 : day === index + 1
        if (!opens || !closes) return { dayOfWeek, opens: "", closes: "", status: "closed", isToday, isOpen: "closed" }
        return {
            dayOfWeek,
            opens,
            closes,
            status: "open",
            isToday,
            isOpen: isToday ? isOpen(now, opens, closes) : "closed",
        }
    })
}
